import { ROLES } from './constants/roles';

export const COMPLIANCE_LEVEL = {
  WARNING: 0,
  RENEWAL: 1,
  OVERDUE: 2,
  RESTRICTED: 3,
};

export const INSPECTION_WARNING_TYPE = 'INSPECTION';

const PROGRAMS = {
  [ROLES.BOAT_MANUFACTURER.serverName]: {
    name: 'Boat/PWC',
    renewalEndMonth: 6,
    overdueEndMonth: 8,
    dueDate: 'June 1st',
    lockedDate: 'August 1st',
    order: 0,
  },
  [ROLES.COMPONENT_MANUFACTURER.serverName]: {
    name: 'Type Accepted',
    renewalEndMonth: 6,
    overdueEndMonth: 8,
    dueDate: 'June 1st',
    lockedDate: 'August 1st',
    order: 1,
  },
  [ROLES.TRAILER_MANUFACTURER.serverName]: {
    name: 'Trailer',
    renewalEndMonth: 8,
    overdueEndMonth: 10,
    dueDate: 'August 1st',
    lockedDate: 'October 1st',
    order: 2,
  },
};

const dateNumber = date => (date.getMonth() + 1) * 100 + date.getDate();

export const getComplianceRoles = user => {
  if (!user || !user.token || user.isInternal() || user.isInspector()) return [];
  return (user.roles || []).filter(role => PROGRAMS[role]);
};

export const getInspectionComplianceRoles = user => getComplianceRoles(user).filter(role =>
  role === ROLES.BOAT_MANUFACTURER.serverName || role === ROLES.TRAILER_MANUFACTURER.serverName
);

export const getComplianceStages = (role, date = new Date()) => {
  const program = PROGRAMS[role];
  if (!program) return [];

  const currentYear = date.getFullYear();
  const currentDate = dateNumber(date);
  const renewalStart = 415;
  const membershipStart = program.renewalEndMonth * 100 + 1;
  const restrictedStart = program.overdueEndMonth * 100 + 1;

  if (currentDate >= renewalStart && currentDate < membershipStart) {
    const renewal = { level: COMPLIANCE_LEVEL.RENEWAL, modelYear: currentYear + 1 };
    return date.getMonth() < 5
      ? [{ level: COMPLIANCE_LEVEL.RESTRICTED, modelYear: currentYear }, renewal]
      : [renewal];
  }
  if (currentDate >= membershipStart && currentDate < restrictedStart) {
    return [{ level: COMPLIANCE_LEVEL.OVERDUE, modelYear: currentYear + 1 }];
  }

  return [{
    level: COMPLIANCE_LEVEL.RESTRICTED,
    modelYear: date.getMonth() < 5 ? currentYear : currentYear + 1,
  }];
};

export const getComplianceStage = (role, date = new Date()) => getComplianceStages(role, date)[0] || null;

const getItemType = item => item.statusType || (item.name || '').toUpperCase();

const getAgreementWarning = (program, level) => {
  if (level === COMPLIANCE_LEVEL.RENEWAL) {
    return {
      title: `${program.name} Annual Agreement Reminder`,
      message: `The ${program.name} Annual Agreement must be signed before ${program.dueDate}. Failure to do so will suspend your access to ECT system until the signature is completed.`,
    };
  }

  const instructions = `The ${program.name} Annual Agreement has not been signed. All Agreements were sent on April 15th. Check your inbox (and spam) on that date for a DocuSign email and follow the link inside that email to electronically sign the Agreement. If you cannot find that email, please contact Lucyna Koscielniak at lkoscielniak@nmma.org.`;
  return {
    title: `${program.name} Annual Agreement Signature Needed`,
    message: level === COMPLIANCE_LEVEL.OVERDUE
      ? `${instructions} Please note your ECT account will be locked as of ${program.lockedDate} due to a lack of a signed agreement.`
      : `${instructions} Please note, your ECT account is now locked due to a lack of signed agreement.`,
  };
};

const getPaymentWarning = (program, level) => {
  if (level === COMPLIANCE_LEVEL.RENEWAL) {
    return {
      title: `${program.name} Annual Fee Reminder`,
      message: `The ${program.name} Annual Certification Fee must be paid before ${program.dueDate}. Failure to do so will suspend your access to ECT system until the payment is completed.`,
    };
  }

  if (level === COMPLIANCE_LEVEL.OVERDUE) {
    return {
      title: `${program.name} Annual Certification Fee Due`,
      message: `The ${program.name} Annual Certification Fee has not been made. Please contact Lucyna Koscielniak at lkoscielniak@nmma.org for your Annual Fee invoice or check your payment portal for any outstanding fees. Please note your ECT account will be locked as of ${program.lockedDate} due to non-payment.`,
    };
  }

  return {
    title: `${program.name} Annual Certification Fee Due`,
    message: `The ${program.name} Annual Certification Fee has not been made. Please contact Lucyna Koscielniak at lkoscielniak@nmma.org for your annual fee invoice or check your payment portal for any outstanding fees. Your account will be unlocked once the payment has been made.`,
  };
};

export const buildComplianceWarnings = ({ role, checklist, date = new Date(), stage: requestedStage }) => {
  const program = PROGRAMS[role];
  const stage = requestedStage || getComplianceStage(role, date);
  if (!program || !stage || !checklist || !Array.isArray(checklist.itemValues)) return [];

  const warningFactories = {
    AGREEMENT: getAgreementWarning,
    PAYMENT: getPaymentWarning,
  };

  const outstandingItems = checklist.itemValues.filter(
    item => item.isApplicable && !item.isChecked && warningFactories[getItemType(item)]
  );
  const hasOutstandingAgreement = outstandingItems.some(item => getItemType(item) === 'AGREEMENT');
  const displayedItems = stage.level === COMPLIANCE_LEVEL.RESTRICTED && hasOutstandingAgreement
    ? outstandingItems.filter(item => getItemType(item) === 'AGREEMENT')
    : outstandingItems;

  return displayedItems
    .sort((left, right) => (getItemType(left) === 'AGREEMENT' ? -1 : 1) - (getItemType(right) === 'AGREEMENT' ? -1 : 1))
    .map(item => ({
      ...warningFactories[getItemType(item)](program, stage.level),
      level: stage.level,
      modelYear: stage.modelYear,
      programOrder: program.order,
      itemType: getItemType(item),
      role,
    }));
};

export const getCurrentModelYear = value => {
  const year = Array.isArray(value) ? (value[1] || value[0]) : value;
  if (year === null || year === undefined || year === '') return null;
  const numericYear = Number(year);
  return Number.isFinite(numericYear) ? numericYear : null;
};

export const buildInspectionWarnings = ({ role, inspections, currentModelYear, date = new Date() }) => {
  const program = PROGRAMS[role];
  const modelYear = getCurrentModelYear(currentModelYear);
  const isInspectionProgram = role === ROLES.BOAT_MANUFACTURER.serverName ||
    role === ROLES.TRAILER_MANUFACTURER.serverName;
  if (!program || !isInspectionProgram || !Array.isArray(inspections) || modelYear === null) {
    return [];
  }

  const currentTime = date.getTime();
  const requiresResponse = inspections.some(inspection => {
    const approvalTime = inspection.approvalDate ? new Date(inspection.approvalDate).getTime() : NaN;
    return Number(inspection.year) === modelYear &&
      inspection.vresponseRequired === true &&
      inspection.response && inspection.response.status !== 'COMPLETE' &&
      Number.isFinite(approvalTime) && currentTime >= approvalTime + 30 * 24 * 60 * 60 * 1000;
  });

  return requiresResponse ? [{
    title: `${program.name} Inspection Variation Response Required`,
    message: `${program.name} inspection for ${modelYear} requires a variation response in order to continue your certification.`,
    level: COMPLIANCE_LEVEL.WARNING,
    modelYear,
    programOrder: program.order,
    itemType: INSPECTION_WARNING_TYPE,
    role,
  }] : [];
};

export const sortComplianceWarnings = warnings => warnings.sort((left, right) =>
  right.level - left.level || left.programOrder - right.programOrder ||
  (left.itemType === 'AGREEMENT' ? -1 : 1) - (right.itemType === 'AGREEMENT' ? -1 : 1)
);





// WEBPACK FOOTER //
// ./src/app/annualCompliance.js